/**
 * Authentication Hook
 *
 * Manages authentication state and fetches current user information.
 * Provides user context and logout functionality.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { api, CurrentUser } from '../services/api';
import { claimStorageFor } from '../utils/storageOwner';

interface UseAuthReturn {
  user: CurrentUser | null;
  loading: boolean;
  error: Error | null;
  logout: () => Promise<void>;
  refetch: () => Promise<void>;
}

const LITE_MODE = import.meta.env.VITE_LITE_MODE === 'true';

const LITE_USER: CurrentUser = {
  id: 'lite-user',
  email: 'dev@localhost',
  displayName: 'Dev (Lite Mode)',
  isAdmin: true,
  // Lite mode has no server to ask, and its stub user stands in for an admin —
  // so it carries the CompassAI seat explicitly rather than having every pill
  // vanish from the offline dev shell.
  hasCompassAi: true,
};

// Shared auth state so multiple useAuth() calls don't each fire /auth/me
let sharedUser: CurrentUser | null = LITE_MODE ? LITE_USER : null;
let sharedLoading = !LITE_MODE;
let sharedError: Error | null = null;
let fetchPromise: Promise<void> | null = null;
const listeners = new Set<() => void>();

function notifyListeners() {
  listeners.forEach((cb) => cb());
}

async function doFetchUser(silent = false) {
  if (LITE_MODE) return;
  // Only show the loading spinner on initial load, not on background
  // refetches (e.g. window-focus).  Setting loading = true during a
  // refetch causes ProtectedLayout to unmount the entire app tree,
  // destroying in-flight component state (upload dialogs, forms, etc.).
  if (!silent) {
    sharedLoading = true;
    sharedError = null;
    notifyListeners();
  }
  try {
    const user = await api.getCurrentUser();
    // Before any page renders: drop a previous user's cached data (utils/storageOwner.ts).
    if (user?.id) claimStorageFor(user.id);
    sharedUser = user;
  } catch (err) {
    sharedError = err instanceof Error ? err : new Error('Failed to fetch user');
    sharedUser = null;
  } finally {
    sharedLoading = false;
    fetchPromise = null;
    notifyListeners();
  }
}

export function useAuth(): UseAuthReturn {
  const [, forceUpdate] = useState(0);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    const listener = () => {
      if (mounted.current) forceUpdate((n) => n + 1);
    };
    listeners.add(listener);

    // Only fetch once — first consumer triggers the request
    if (!fetchPromise && sharedLoading) {
      fetchPromise = doFetchUser();
    }

    return () => {
      mounted.current = false;
      listeners.delete(listener);
    };
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.logout();
    } catch {
      // Logout endpoint may fail if session already expired — that's fine
    }
    sharedUser = null;
    sharedLoading = false;
    notifyListeners();
    window.location.href = '/login';
  }, []);

  const refetch = useCallback(async () => {
    if (!fetchPromise) {
      fetchPromise = doFetchUser(true);
    }
    await fetchPromise;
  }, []);

  return {
    user: sharedUser,
    loading: sharedLoading,
    error: sharedError,
    logout,
    refetch,
  };
}
